import { Locales } from '@/utils/translations/types/TypeLanguagesAndLocales';
import moment from 'moment';
import {
  ChildBirthDateInput,
  DisplayDateFormat,
  TypeDisplayDateFormats,
} from '@/utils/dates/constants';
import 'moment/min/locales';

export function formatDateForDisplay(
  dateString: string,
  locale: string | undefined,
  format?: TypeDisplayDateFormats,
) {
  const date = moment(dateString).toDate();

  if (!_isValidDate(date)) {
    console.warn(
      `[formatDateForDisplay] unable to format provided dateString ${dateString} for locale ${locale}.`,
    );
    return dateString;
  }

  const formatter = _getFormatter(locale, format);
  return formatter.format(date);
}

export function childBirthDateToDateString(
  childBirthDate: ChildBirthDateInput,
) {
  return `${childBirthDate.year}-${childBirthDate.month}-${childBirthDate.day}`;
}

// a Date object whose toString() method returns "Invalid Date" and valueOf() method returns NaN
// see @link{https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date#return_value}
function _isValidDate(date: Date) {
  const isInvalidDate =
    date.toString() === 'Invalid Date' && isNaN(date.valueOf());

  return !isInvalidDate;
}

function _getFormatter(
  locale: string | undefined,
  formatType?: TypeDisplayDateFormats,
) {
  switch (formatType) {
    case DisplayDateFormat.NumericMonthDayYear:
      return new Intl.DateTimeFormat(_getValidatedLocale(locale), {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    case DisplayDateFormat.ShortMonthDayYear:
      return new Intl.DateTimeFormat(_getValidatedLocale(locale), {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    case DisplayDateFormat.ShortMonthAndDay:
      return new Intl.DateTimeFormat(_getValidatedLocale(locale), {
        month: 'short',
        day: 'numeric',
      });
    default:
      return new Intl.DateTimeFormat(_getValidatedLocale(locale));
  }
}

function _getValidatedLocale(locale: string | undefined) {
  switch (locale) {
    case Locales.UnitedStatesEnglish:
      return Locales.UnitedStatesEnglish;
    case Locales.AustralianEnglish:
      return Locales.AustralianEnglish;
    // note en-EU is not a valid iso language code, formatters should convert to en-150 for accurate localization.
    // @see {https://www.localeplanet.com/icu/en-150/index.html} for more information
    case Locales.EuropeanEnglish:
      return 'en-150';
    case Locales.UnitedKingdomEnglish:
      return Locales.UnitedKingdomEnglish;
    case Locales.StandardDutch:
      return Locales.StandardDutch;
    case Locales.StandardFrench:
      return Locales.StandardFrench;
    case Locales.StandardGerman:
      return Locales.StandardGerman;
    case Locales.StandardSpanish:
      return Locales.StandardSpanish;
    default:
      return 'default';
  }
}

export enum DATE_FORMATS {
  'MMM D, YYYY' = 'MMM D, YYYY',
  'D MMM YYYY' = 'D MMM YYYY',
  'MMM DD' = 'MMM DD',
}

export function formatDate(
  date: string | undefined,
  format: keyof typeof DATE_FORMATS,
  locale: string,
) {
  moment.locale(locale);
  return date ? moment(date).format(DATE_FORMATS[format]) : undefined;
}

export function parseDigiohDob(digiohDobValue: string) {
  const [year, month, day] = digiohDobValue.split('-');
  if (!day || !month || !year)
    return {
      month: '',
      day: '',
      year: '',
    };

  const formattedDay = day.startsWith('0') ? day.slice(1) : day;

  const formattedDigiohDob = {
    month: month,
    day: formattedDay,
    year: year,
  };

  return formattedDigiohDob;
}
